// invoiceFinanceSourcing.js
// Maps Invoice Finance API rows → sourcing result cards.
// Includes full calculator logic: facility auto-calc, net advance,
// cost over product term, and additional borrowing calculations.

/** Canonical sector options for IF sourcing (must match backend `sector` values). */
export const INVOICE_FINANCE_SECTORS = [
  "Recruitment and staffing",
  "Manufacturing and engineering",
  "Transport, Logistics and Haulage",
  "Construction and property services",
  "Wholesale, distribution and retail",
  "Professional and business services",
  "Healthcare and care services",
  "Agriculture, food and environmental services",
  "Printing, media and creative industries",
  "Security, facilities and support services",
];

/** One-line blurbs shown on sector info tips in the sourcing dropdown. */
export const INVOICE_FINANCE_SECTOR_BLURBS = {
  "Recruitment and staffing":
    "Agencies supplying temporary or permanent staff to client businesses.",
  "Manufacturing and engineering":
    "Firms that manufacture, fabricate or engineer physical goods and components.",
  "Transport, Logistics and Haulage":
    "Road haulage, freight, warehousing and logistics operators.",
  "Construction and property services":
    "Builders, contractors and property-related trade or maintenance services.",
  "Wholesale, distribution and retail":
    "Wholesalers, distributors and retailers selling goods to trade or consumers.",
  "Professional and business services":
    "Consultancies, agencies and other B2B professional service firms.",
  "Healthcare and care services":
    "Care providers, clinics and healthcare support businesses.",
  "Agriculture, food and environmental services":
    "Farming, food production and environmental or waste service businesses.",
  "Printing, media and creative industries":
    "Print, publishing, media production and creative agencies.",
  "Security, facilities and support services":
    "Security, cleaning, facilities management and business support firms.",
};

/** Map short / legacy labels onto the canonical sector list. */
const INVOICE_FINANCE_SECTOR_ALIASES = {
  haulage: "Transport, Logistics and Haulage",
  transport: "Transport, Logistics and Haulage",
  logistics: "Transport, Logistics and Haulage",
  "transport logistics and haulage": "Transport, Logistics and Haulage",
  "transport, logistics and haulage": "Transport, Logistics and Haulage",
  engineering: "Manufacturing and engineering",
  manufacturing: "Manufacturing and engineering",
  "manufacturing and engineering": "Manufacturing and engineering",
  recruitment: "Recruitment and staffing",
  staffing: "Recruitment and staffing",
  "recruitment and staffing": "Recruitment and staffing",
  professional: "Professional and business services",
  "business services": "Professional and business services",
  "professional and business services": "Professional and business services",
  construction: "Construction and property services",
  property: "Construction and property services",
  "property services": "Construction and property services",
  "construction and property services": "Construction and property services",
  wholesale: "Wholesale, distribution and retail",
  distribution: "Wholesale, distribution and retail",
  retail: "Wholesale, distribution and retail",
  "wholesale distribution and retail": "Wholesale, distribution and retail",
  healthcare: "Healthcare and care services",
  care: "Healthcare and care services",
  "care services": "Healthcare and care services",
  "healthcare and care services": "Healthcare and care services",
  agriculture: "Agriculture, food and environmental services",
  food: "Agriculture, food and environmental services",
  environmental: "Agriculture, food and environmental services",
  "agriculture food and environmental services":
    "Agriculture, food and environmental services",
  printing: "Printing, media and creative industries",
  media: "Printing, media and creative industries",
  creative: "Printing, media and creative industries",
  "printing media and creative industries": "Printing, media and creative industries",
  security: "Security, facilities and support services",
  facilities: "Security, facilities and support services",
  "support services": "Security, facilities and support services",
  "security facilities and support services":
    "Security, facilities and support services",
};

const SECTOR_STOP_WORDS = new Set(["and", "or", "the", "of", "a", "an", "&"]);

function normalizeSectorKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[_/]+/g, " ")
    .replace(/[^\w\s,&-]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Significant tokens for fuzzy sector matching (drops "and"/commas/noise). */
function sectorTokens(value) {
  return normalizeSectorKey(value)
    .split(/[\s,]+/)
    .filter((t) => t && !SECTOR_STOP_WORDS.has(t) && t.length > 1);
}

/**
 * Score how well candidate tokens map onto a canonical sector.
 * Requires every candidate token to appear in the canonical label (abbreviation/subset).
 */
function scoreSectorTokenMatch(candidateTokens, canonicalTokens) {
  if (!candidateTokens.length || !canonicalTokens.length) return 0;
  const canonSet = new Set(canonicalTokens);
  const matched = candidateTokens.filter((t) => canonSet.has(t));
  if (matched.length === 0 || matched.length !== candidateTokens.length) return 0;
  let score = matched.length * 10;
  if (candidateTokens[0] === canonicalTokens[0]) score += 5;
  score += matched.length / canonicalTokens.length;
  return score;
}

/** Best unambiguous canonical sector for a free-text / short API label. */
function smartResolveSector(raw) {
  const tokens = sectorTokens(raw);
  if (!tokens.length) return "";

  let best = "";
  let bestScore = 0;
  let tied = false;

  for (const canonical of INVOICE_FINANCE_SECTORS) {
    const score = scoreSectorTokenMatch(tokens, sectorTokens(canonical));
    if (score > bestScore) {
      bestScore = score;
      best = canonical;
      tied = false;
    } else if (score > 0 && score === bestScore) {
      tied = true;
    }
  }

  return best && bestScore > 0 && !tied ? best : "";
}

/** Display label: Title Case words, "and" → "&". Value sent to API stays unchanged. */
export function formatInvoiceFinanceSectorLabel(sector) {
  const raw = String(sector || "").trim();
  if (!raw) return "";
  return raw
    .replace(/\band\b/gi, "&")
    .replace(/[A-Za-zÀ-ÿ]+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

/**
 * Resolve any sector label (full, short, or legacy) to the canonical dropdown value.
 * e.g. "Recruitment" → "Recruitment and staffing"
 */
export function resolveInvoiceFinanceSector(sector) {
  const raw = String(sector || "").trim();
  if (!raw) return "";
  const key = normalizeSectorKey(raw);
  const exact = INVOICE_FINANCE_SECTORS.find((s) => normalizeSectorKey(s) === key);
  if (exact) return exact;
  const alias = INVOICE_FINANCE_SECTOR_ALIASES[key];
  if (alias) return alias;
  return smartResolveSector(raw) || raw;
}

/** True when the selected sector is Construction (home-owner filter applies). */
export function isInvoiceFinanceConstructionSector(sector) {
  return (
    resolveInvoiceFinanceSector(sector) === "Construction and property services"
  );
}

/**
 * True when product sector matches the selected sourcing sector.
 * Handles short API labels vs full frontend names across all sectors
 * (e.g. product "Recruitment" ↔ selected "Recruitment and staffing").
 */
export function matchesInvoiceFinanceSector(productSector, selectedSector) {
  const selected = String(selectedSector || "").trim();
  if (!selected) return true;

  const productCanonical = resolveInvoiceFinanceSector(productSector);
  const selectedCanonical = resolveInvoiceFinanceSector(selected);

  if (
    normalizeSectorKey(productCanonical) === normalizeSectorKey(selectedCanonical)
  ) {
    return true;
  }

  const productTokens = sectorTokens(productSector);
  const selectedTokens = sectorTokens(selectedCanonical || selected);
  if (!productTokens.length || !selectedTokens.length) return false;

  // Short API label ⊆ selected sector tokens
  if (productTokens.every((t) => selectedTokens.includes(t))) return true;
  // Selected short label ⊆ product sector tokens
  if (selectedTokens.every((t) => productTokens.includes(t))) return true;

  return false;
}

/** Backend test rows — hide from sector dropdowns. */
export function isExcludedInvoiceFinanceSector(sector) {
  return String(sector || "").trim().toLowerCase() === "test";
}

export function filterInvoiceFinanceSectorOptions(sectors) {
  return (sectors || [])
    .map((s) => resolveInvoiceFinanceSector(s) || s)
    .filter((s) => s && !isExcludedInvoiceFinanceSector(s))
    .filter((s, i, arr) => arr.findIndex((x) => normalizeSectorKey(x) === normalizeSectorKey(s)) === i);
}

// ─── Formula chain ────────────────────────────────────────────────────────────
// Invoice value  = round(Turnover × (creditTermsDays / 365))
// Facility       = round(Turnover × (creditTermsDays / 365) × 1.2)  — VAT uplift
export function calcFacilityFromTurnover(turnoverNum, creditTermsDays) {
  const turnover = Math.max(0, Number(turnoverNum) || 0);
  // Values below 30 days (including 0) use the same economics as 30 days.
  const rawDays = Number(creditTermsDays);
  const creditDays = !Number.isFinite(rawDays) || rawDays < 30 ? 30 : rawDays;
  if (turnover <= 0) {
    return { invoiceValue: 0, facilityNum: 0 };
  }

  const invoiceValue = Math.round(turnover * (creditDays / 365));
  const facilityNum = Math.round(turnover * (creditDays / 365) * 1.2);

  return { invoiceValue, facilityNum };
}

/** Effective credit days for facility/eligibility maths (floor at 30). */
export function effectiveCreditTermsDays(creditTermsDays) {
  const rawDays = Number(creditTermsDays);
  if (!Number.isFinite(rawDays) || rawDays < 30) return 30;
  return rawDays;
}

// ─── Additional borrowing helpers ─────────────────────────────────────────────

/** Standard amortisation monthly payment (Capital Repayment). */
function monthlyCapitalPayment(principal, annualRatePct, months) {
  const p = Number(principal) || 0;
  const m = Number(months) || 0;
  if (!p || !m) return 0;
  const annualRate = annualRatePct / 100;
  if (!annualRate) return p / m;
  const r = annualRate / 12;
  return p * (r * Math.pow(1 + r, m)) / (Math.pow(1 + r, m) - 1);
}

/**
 * Calculate additional borrowing outputs for a single product.
 *
 * DB fields used:
 *   additional_borrowing          "Yes" | "No"
 *   additional_borrowing_amount   £ amount (string | null)  — fixed loan amount from DB
 *   interest_rate                 annual % (e.g. 2.85)
 *   io_or_capital_repayment       "Capital Repayment" | "" (empty = Interest Only)
 *   arrangement_fee_term          AB arrangement fee as % of AB principal (e.g. "1" = 1%)
 *                                 (distinct from product arrangement_fee used for IF facility costs)
 *   term_months                   0 in current data → fall back to contract_term_months
 *   contract_term_months          e.g. 12 or 24
 *
 * User inputs (required — no DB fallback):
 *   abValue                       user-entered additional borrowing value
 *   abType                        "£" | "%"
 */
/** Max additional borrowing (£) supported by a product, from DB `additional_borrowing_amount`. */
export function getProductMaxAbAmount(product) {
  const raw = product?._raw || product || {};
  return readNumber(
    product?.additionalBorrowingAmount,
    product?.additional_borrowing_amount,
    raw.additional_borrowing_amount
  );
}

export function calcAdditionalBorrowing(product, { facilityNum, abValue, abType }) {
  const raw = product?._raw || product || {};

  const hasAB = (product?.additional_borrowing || raw.additional_borrowing) === "Yes";
  if (!hasAB) return null;

  const facility = Number(facilityNum) || 0;
  const userVal = parseFloat(String(abValue || "").replace(/,/g, "")) || 0;
  if (userVal <= 0) return null;

  const abPrincipal = abType === "%" ? Math.round(facility * userVal / 100) : Math.round(userVal);
  if (!abPrincipal) return null;

  const interestRatePct = Number(product?.interest_rate ?? raw.interest_rate) || 0;

  // io_or_capital_repayment: "Capital Repayment" → C&I, anything else → Interest Only
  const repaymentTypeRaw = String(
    product?.io_or_capital_repayment ?? raw.io_or_capital_repayment ?? ""
  ).trim();
  const isCapitalRepayment = repaymentTypeRaw === "Capital Repayment";
  const repaymentType = isCapitalRepayment ? "Capital & Interest" : "Interest Only";

  // term_months: 0 in DB → fall back to contract_term_months
  const termMonthsRaw = Number(product?.term_months ?? raw.term_months) || 0;
  const contractTerm = Number(product?.contract_term_months ?? raw.contract_term_months) || 12;
  const termMonths = termMonthsRaw > 0 ? termMonthsRaw : contractTerm;

  // AB arrangement fee only — do not use product arrangement_fee here
  const arrFeePct = Number(
    product?.arrangement_fee_term ?? raw.arrangement_fee_term ?? product?.arrangementFeeTerm
  );
  const arrangementFeePct = Number.isFinite(arrFeePct) && Math.abs(arrFeePct) <= 100
    ? arrFeePct
    : 0;
  const arrangementFee = Math.round(abPrincipal * (arrangementFeePct / 100));

  const annualInterest = Math.round(abPrincipal * interestRatePct / 100);

  let monthlyRepayment, totalRepayable, costOverTerm;

  if (isCapitalRepayment) {
    // Standard amortisation
    monthlyRepayment = monthlyCapitalPayment(abPrincipal, interestRatePct, termMonths);
    // Total repayable = (monthly × term) + arrangement fee
    totalRepayable = Math.round(monthlyRepayment * termMonths) + arrangementFee;
    // Cost over product term = total repayable − principal
    costOverTerm = totalRepayable - abPrincipal;
  } else {
    // Interest Only
    monthlyRepayment = Math.round(abPrincipal * interestRatePct / 100 / 12);
    // Total repayable = (monthly × term) + principal (balloon) + arrangement fee
    totalRepayable = Math.round(monthlyRepayment * termMonths) + abPrincipal + arrangementFee;
    // Cost over product term = total repayable − principal
    costOverTerm = totalRepayable - abPrincipal;
  }

  return {
    abPrincipal,
    interestRatePct,
    repaymentType,
    termMonths,
    arrangementFeePct,
    arrangementFee,
    annualInterest,
    monthlyRepayment: Math.round(monthlyRepayment),
    totalRepayable,
    costOverTerm,
  };
}

// ─── Helper: read first finite number from a list of candidates ───────────────
function readNumber(...values) {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Normalize percent fields stored either as whole percentages (90, 40)
 * or decimal fractions (0.9, 0.4) onto a 0–100 scale.
 */
export function normalizePercentScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n === 0) return 0;
  // Values in (0, 1] are decimal fractions (0.9 → 90%, 1 → 100%).
  if (n > 0 && n <= 1) return n * 100;
  return n;
}

/** Read the first finite number from candidates and normalize to a 0–100 percent scale. */
function readPercentScale(...values) {
  const raw = readNumber(...values);
  return raw == null ? null : normalizePercentScale(raw);
}

/** Read min/max facility band from a mapped row or raw API product. */
export function readProductLimitBand(row) {
  const raw = row?._raw || row?.sourceProduct || row || {};
  const min = readNumber(
    row?.minLimit,
    row?.min_limit,
    raw?.minLimit,
    raw?.min_limit,
    row?.sourceProduct?.min_limit,
    row?.sourceProduct?.minLimit
  );
  const max = readNumber(
    row?.maxLimit,
    row?.max_limit,
    raw?.maxLimit,
    raw?.max_limit,
    row?.sourceProduct?.max_limit,
    row?.sourceProduct?.maxLimit
  );
  return { min, max };
}

/**
 * True when the required facility sits inside the product's min/max limit band (inclusive).
 * `min_limit` / `max_limit` on IF products are facility windows, not turnover.
 */
export function productMatchesFacilityBand(row, facilityNum) {
  const facility = Number(facilityNum) || 0;
  if (facility <= 0) return true;

  const { min, max } = readProductLimitBand(row);
  if (min == null && max == null) return true;
  const lo = min == null ? -Infinity : min;
  const hi = max == null ? Infinity : max;
  if (lo > hi) return false;
  return facility >= lo && facility <= hi;
}

function pickInvoiceFinanceField(row, keys) {
  const raw = row?._raw || {};
  const source = row?.sourceProduct || {};
  const bags = [row, raw, source, source._raw, raw._raw];
  for (const bag of bags) {
    if (!bag || typeof bag !== "object") continue;
    for (const key of keys) {
      const value = bag[key];
      if (value != null && String(value).trim() !== "") return value;
    }
  }
  return undefined;
}

/** Lower rank = stronger credit. Poor includes Good/OK/Fair products. */
const INVOICE_FINANCE_CREDIT_RANK = {
  excellent: 0,
  good: 0,
  ok: 1,
  okay: 1,
  average: 1,
  fair: 2,
  poor: 3,
  bad: 3,
};

export function invoiceFinanceCreditRank(value) {
  if (value == null || String(value).trim() === "") return null;
  const s = String(value).trim().toLowerCase();
  if (s in INVOICE_FINANCE_CREDIT_RANK) return INVOICE_FINANCE_CREDIT_RANK[s];
  if (s.startsWith("excellent") || s.startsWith("good")) return 0;
  if (s.startsWith("ok") || s.startsWith("okay") || s.startsWith("average")) return 1;
  if (s.startsWith("fair")) return 2;
  if (s.startsWith("poor") || s.startsWith("bad")) return 3;
  return null;
}

function invoiceFinanceCreditRanks(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (item && typeof item === "object") {
        return invoiceFinanceCreditRanks(
          item.credit_worthiness || item.credit || item.value || item.name
        );
      }
      return invoiceFinanceCreditRanks(item);
    });
  }
  return String(value)
    .split(/[,/;|]+/)
    .map((part) => invoiceFinanceCreditRank(part))
    .filter((rank) => rank != null);
}

function productMatchesInvoiceCredit(row, userCredit) {
  const userRank = invoiceFinanceCreditRank(userCredit);
  if (userRank == null) return true;
  const productValue = pickInvoiceFinanceField(row, [
    "creditWorthiness",
    "credit_worthiness",
    "credit_worthiness_list",
    "credit_worth",
    "creditWorth",
    "credit",
  ]);
  const ranks = invoiceFinanceCreditRanks(productValue);
  if (!ranks.length) return true;
  return ranks.some((rank) => rank <= userRank);
}

/** Yes includes both values; No only matches the No/disclosed side. */
function productMatchesInclusiveYesFlag(productIsYes, userWantsYes) {
  if (userWantsYes) return true;
  return !productIsYes;
}

function isExplicitYesNoFilter(value) {
  if (value === true || value === false || value === 1 || value === 0) return true;
  const s = String(value ?? "").trim().toLowerCase();
  return s === "yes" || s === "no" || s === "1" || s === "0" || s === "true" || s === "false";
}

function matchesInclusiveYesNoFilter(productIsYes, userValue) {
  if (!isExplicitYesNoFilter(userValue)) return true;
  return productMatchesInclusiveYesFlag(productIsYes, toFlag01(userValue) === 1);
}

/** Soft eligibility checks applied client-side after catalogue fetch. */
export function productMatchesInvoiceEligibility(row, filters = {}) {
  const raw = row?._raw || row?.sourceProduct || row || {};

  if (!productMatchesInvoiceCredit(row, filters.creditWorthiness ?? filters.credit_worthiness)) {
    return false;
  }

  if (
    !matchesInclusiveYesNoFilter(
      isInvoiceFinanceConfidential(row),
      filters.confidentialFacility ?? filters.confidential_facility
    )
  ) {
    return false;
  }

  const hmrcYes =
    toFlag01(
      pickInvoiceFinanceField(row, ["hmrcArrears", "hmrc_arrears", "hmrc"])
    ) === 1;
  if (
    !matchesInclusiveYesNoFilter(
      hmrcYes,
      filters.hmrcArrears ?? filters.hmrc_arrears
    )
  ) {
    return false;
  }

  const liquidationsYes =
    toFlag01(
      pickInvoiceFinanceField(row, [
        "previousLiquidatedCo",
        "previous_liquidated_co",
        "previous_liquidations",
        "previousLiquidations",
      ])
    ) === 1;
  if (
    !matchesInclusiveYesNoFilter(
      liquidationsYes,
      filters.previousLiquidatedCo ?? filters.previous_liquidated_co
    )
  ) {
    return false;
  }

  const creditDays = effectiveCreditTermsDays(filters.creditTermsDays);
  if (Number.isFinite(creditDays) && creditDays > 0) {
    const maxCredit = readNumber(
      row?.maxCreditTerms,
      raw?.max_credit_terms,
      row?.sourceProduct?.max_credit_terms
    );
    if (maxCredit != null && creditDays > maxCredit) return false;
  }

  const contractTerm = Number(filters.contractTermMonths);
  if (Number.isFinite(contractTerm) && contractTerm > 0) {
    const productTerm = readNumber(
      row?.termMonths,
      row?.contract_term_months,
      raw?.contract_term_months,
      row?.sourceProduct?.contract_term_months
    );
    if (productTerm != null && productTerm !== contractTerm) return false;
  }

  const concentration = Number(filters.concentrationLimit);
  if (Number.isFinite(concentration)) {
    const maxConcen = readPercentScale(
      raw?.max_concen_percent,
      row?.maxConcenPercent,
      row?.sourceProduct?.max_concen_percent
    );
    if (maxConcen != null && concentration > maxConcen) return false;
  }

  const exportLedger = Number(filters.exportLedger);
  if (Number.isFinite(exportLedger)) {
    // Export ledger filter is matched against Max. Ledger DP (not Export Max).
    const maxLedgerDp = readPercentScale(
      raw?.max_ledger_dp,
      raw?.max_ledger_dp_percent,
      row?.maxLedgerDpPercent,
      row?.sourceProduct?.max_ledger_dp,
      row?.sourceProduct?.max_ledger_dp_percent
    );
    if (maxLedgerDp != null && exportLedger > maxLedgerDp) return false;
  }

  // Construction home-owner rule (mirrors BTL sourcing):
  // - "Yes" / empty => no restriction
  // - "No" => only products explicitly marked No
  const homeOwner = String(filters.homeOwner || "").trim().toLowerCase();
  if (homeOwner === "no") {
    const productHomeOwner = String(
      row?.homeOwner ?? row?.home_owner ?? raw?.home_owner ?? raw?.homeOwner ?? ""
    )
      .trim()
      .toLowerCase();
    if (productHomeOwner !== "no") return false;
  }

  return true;
}

/** @deprecated Use productMatchesFacilityBand — limits are facility bands. */
export function productMatchesTurnoverBand(row, turnoverNum) {
  return productMatchesFacilityBand(row, turnoverNum);
}

// ─── Cost over product term estimate (IF facility only, no additional borrowing) ─
function estimateCostOverProductTerm(advance, discountRate, serviceFee, minServiceFee, termMonths) {
  const adv = Number(advance) || 0;
  const disc = Number(discountRate) || 0;
  const svc = Number(serviceFee) || 0;
  const mini = Number(minServiceFee) || 0;
  const months = Number(termMonths) || 12;
  const termYears = Math.max(months / 12, 0.25);
  const discountCost = adv * (disc / 100) * termYears;
  const serviceCost = adv * (svc / 100) * termYears;
  return Math.round(discountCost + serviceCost + mini);
}

// ─── Main mapper ──────────────────────────────────────────────────────────────
/**
 * Map a single API product row to a sourcing result card row.
 *
 * @param {object} product   Raw API product object
 * @param {object} context
 *   facilityNum   — auto-calculated (Turnover × creditTerms/365 × 1.2)
 *   turnoverNum   — projected turnover entered by user
 *   invoiceValue  — background calc (Turnover × creditTerms/365), not shown in UI
 *   abValue       — additional borrowing value entered by user ("" | number string)
 *   abType        — "£" | "%"
 */
export function mapInvoiceFinanceToSourcingRow(product, { facilityNum, turnoverNum, abValue = "", abType = "£" } = {}) {
  const raw = product?._raw || product || {};

  const id = String(
    product?.name || product?._id || product?.productId || product?.id || raw.name || ""
  ).trim();

  const ipPercent =
    readPercentScale(product?.invoicePurchasePct, product?.ip_percent, raw.ip_percent) ?? 0;

  const discountRate =
    readNumber(product?.ratePct, product?.discount_rate_percent, raw.discount_rate_percent) ?? 0;

  const serviceFee =
    readNumber(product?.annualCostPct, product?.service_fee_percent, raw.service_fee_percent) ?? 0;

  // min_service_fee: stored as £ amount in DB (e.g. 18000)
  const minServiceFee =
    readNumber(product?.miniServiceFeePct, product?.min_service_fee, raw.min_service_fee) ?? 0;

  const termMonths =
    readNumber(product?.termMonths, product?.contract_term_months, raw.contract_term_months) ?? 12;

  const facility = Math.max(0, Number(facilityNum) || 0);
  const turnover = Math.max(0, Number(turnoverNum) || 0);

  // Net advance = facility × (IP% / 100)
  const netAdvance =
    readNumber(product?.netAdvance, product?.net_advance) ??
    Math.max(0, Math.round(facility * (ipPercent / 100)));

  const costOverProductTerm =
    readNumber(product?.costOverProductTerm, product?.cost_over_product_term) ??
    estimateCostOverProductTerm(netAdvance, discountRate, serviceFee, minServiceFee, termMonths);

  const headlineFromApi = readNumber(
    product?.headlineAmount,
    product?.headline_amount,
    product?.headline_facility
  );
  const headlineAmount = headlineFromApi != null ? headlineFromApi : facility || netAdvance || 0;

  // Additional borrowing — calculated from real DB fields + user input
  const abCalc = calcAdditionalBorrowing(product, { facilityNum: facility, abValue, abType });

  return {
    id,
    lenderName:
      product?.lenderName ||
      product?.lender_name ||
      product?.lender_product_code ||
      raw.lender_product_code ||
      raw.lender_name ||
      id,
    sector: product?.sector || raw.sector || "—",
    invoicePurchasePct: ipPercent,
    maxConcenPercent: readPercentScale(
      product?.maxConcenPercent,
      product?.max_concen_percent,
      raw.max_concen_percent
    ),
    maxLedgerDpPercent: readPercentScale(
      product?.maxLedgerDpPercent,
      product?.max_ledger_dp,
      product?.max_ledger_dp_percent,
      raw.max_ledger_dp,
      raw.max_ledger_dp_percent
    ),
    exportMaxPercent: readPercentScale(
      product?.exportMaxPercent,
      product?.export_max,
      raw.export_max
    ),
    ratePct: discountRate,
    headlineAmount,
    termMonths,
    dpAllowed: product?.dpAllowed || product?.dp_allowed || raw.dp_allowed || "Yes",
    additionalBorrowing: (product?.additional_borrowing ?? raw.additional_borrowing) === "Yes",
    annualCostPct: serviceFee,
    totalCostPct:
      readNumber(product?.totalCostPct, product?.total_cost_pct) ??
      Math.round((serviceFee + discountRate) * 1e6) / 1e6,
    netAdvance: netAdvance || 1,
    miniServiceFeePct: minServiceFee,
    costOverProductTerm: Math.max(costOverProductTerm, 1),
    minLimit: readNumber(product?.minLimit, product?.min_limit, raw.min_limit),
    maxLimit: readNumber(product?.maxLimit, product?.max_limit, raw.max_limit),
    maxAbAmount: getProductMaxAbAmount(product),
    // Additional borrowing calculated outputs — null if product doesn't support it
    abCalc,
    _raw: raw,
    _normalized: product,
  };
}

/** Parse schedule datetime; supports DD-MM-YYYY HH:MM:SS and ISO-ish strings. */
function parseInvoiceFinanceScheduleDate(scheduleDate) {
  if (!scheduleDate) return null;
  try {
    let dateStr = String(scheduleDate).trim();
    if (!dateStr) return null;

    if (dateStr.match(/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}$/)) {
      const parts = dateStr.split(" ");
      const datePart = parts[0].split("-");
      const timePart = parts[1] || "00:00:00";
      dateStr = `${datePart[2]}-${datePart[1]}-${datePart[0]}T${timePart}`;
    } else if (dateStr.includes(" ") && !dateStr.includes("T")) {
      dateStr = dateStr.replace(" ", "T");
    }

    const parsed = new Date(dateStr);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

export function hasInvoiceFinanceScheduleDatePassed(product) {
  const scheduleDate =
    product?.date_time ||
    product?.product_schedule_at ||
    product?.schedule_at ||
    product?.scheduled_at ||
    product?.product_schedule_date ||
    product?.schedule_date ||
    product?.scheduled_date ||
    product?._raw?.date_time;

  const parsed = parseInvoiceFinanceScheduleDate(scheduleDate);
  if (!parsed) return false;
  return parsed <= new Date();
}

function invoiceFinanceStatusLower(product) {
  const raw = product?.product_status ?? product?.status ?? product?._raw?.status ?? "";
  return String(raw || "").trim().toLowerCase();
}

/** Deactivated / disabled products must not appear on IF sourcing. */
export function isInvoiceFinanceProductDeactivated(product) {
  const disabled = product?.disabled ?? product?._raw?.disabled;
  if (disabled === 1 || disabled === true || String(disabled).toLowerCase() === "yes") {
    return true;
  }
  const status = invoiceFinanceStatusLower(product);
  return ["deactivated", "deactivate", "inactive", "disabled"].includes(status);
}

/**
 * Scheduled (coming soon) products: status Scheduled, or product_schedule flag
 * with a future date. Past schedule dates are treated as live.
 */
export function isInvoiceFinanceProductScheduled(product) {
  if (product?._isScheduled === true) {
    return !hasInvoiceFinanceScheduleDatePassed(product);
  }

  const status = invoiceFinanceStatusLower(product);
  if (status === "scheduled" || status === "schedule") {
    return !hasInvoiceFinanceScheduleDatePassed(product);
  }

  const scheduleFlag =
    product?.product_schedule ?? product?._raw?.product_schedule;
  const hasScheduleFlag =
    scheduleFlag === 1 ||
    scheduleFlag === true ||
    String(scheduleFlag || "").toLowerCase() === "true" ||
    String(scheduleFlag || "") === "1";

  return hasScheduleFlag && !hasInvoiceFinanceScheduleDatePassed(product);
}

/** Drop deactivated products; keep Active + future Scheduled. */
export function filterInvoiceFinanceSourcingProducts(products) {
  return (products || []).filter((p) => !isInvoiceFinanceProductDeactivated(p));
}

function toFlag01(value) {
  if (value === true || value === 1 || value === "1") return 1;
  if (value === false || value === 0 || value === "0") return 0;
  const s = String(value || "").trim().toLowerCase();
  if (s === "yes" || s === "true") return 1;
  if (s === "no" || s === "false" || s === "") return 0;
  return 0;
}

/** True when IF facility is confidential (vs disclosure). */
export function isInvoiceFinanceConfidential(source = {}) {
  const raw = source._raw || source;
  const facilityType = String(
    source.facility_type ?? source.facilityType ?? raw.facility_type ?? ""
  )
    .trim()
    .toLowerCase();
  if (facilityType === "confidential") return true;
  if (facilityType === "disclosure" || facilityType === "disclosed") return false;
  // Ignore legacy Part/Full ledger values stored in facility_type
  if (facilityType === "part" || facilityType === "full" || facilityType.includes("invoice")) {
    // fall through to flags
  }
  return (
    toFlag01(
      source.confidential_facility ??
        source.confidentialFacility ??
        source.confidential ??
        raw.confidential_facility ??
        raw.confidential
    ) === 1
  );
}

/** Shortlist/application facility_type: Confidential | Disclosure */
export function resolveInvoiceFinanceFacilityType(source = {}) {
  return isInvoiceFinanceConfidential(source) ? "Confidential" : "Disclosure";
}

/** Resolve IF facility contract term (months). Prefer contract_term_months only — never AB term or mortgage_term. */
export function resolveInvoiceFinanceContractTermMonths(source = {}) {
  const raw = source?._raw || source || {};
  const candidates = [
    source.contract_term_months,
    raw.contract_term_months,
    // Sourcing card rows store facility term on termMonths (not AB)
    source.termMonths,
    source.term_months,
    raw.termMonths,
    raw.term_months,
  ];
  for (const value of candidates) {
    const months = Number(value);
    if (Number.isFinite(months) && months > 0) return Math.round(months);
  }
  return null;
}

/** Cart / application top title: "Confidential - 12 months" | "Disclosed - 12 months" */
export function formatInvoiceFinanceProductTitle(source = {}) {
  const label = isInvoiceFinanceConfidential(source) ? "Confidential" : "Disclosed";
  const months = resolveInvoiceFinanceContractTermMonths(source);
  const term =
    months != null ? `${months} month${months === 1 ? "" : "s"}` : "—";
  return `${label} - ${term}`;
}

/**
 * Build shortlist/apply payload for an Invoice Finance sourcing card.
 * Uses the same shortlist API shape as BTL, with IF Application fields populated.
 */
export function buildInvoiceFinanceShortlistProductData(row, context = {}) {
  const {
    sector = "",
    projectedTurnover = 0,
    facilityRequired = 0,
    additionalBorrowingNum = 0,
    additionalBorrowingType = "amount",
    filters = {},
  } = context;

  const raw = row?._raw || row?.sourceProduct || row || {};
  const id = String(row?.id || raw?.name || row?.name || "").trim();
  const facility = Math.max(0, Number(facilityRequired) || 0);
  const turnover = Math.max(0, Number(projectedTurnover) || 0);

  const ipPercent =
    normalizePercentScale(
      row?.invoicePurchasePct ?? row?.ip_percent ?? raw?.ip_percent
    ) ?? 0;
  const discountRatePercent =
    normalizePercentScale(
      row?.discountRatePct ??
        row?.ratePct ??
        row?.discount_rate_percent ??
        raw?.discount_rate_percent
    ) ?? 0;
  const simpleServiceFee =
    normalizePercentScale(
      row?.annualCostPct ??
        row?.serviceFeePct ??
        row?.service_fee_percent ??
        raw?.service_fee_percent
    ) ?? 0;
  const minServiceFee =
    readNumber(
      row?.miniServiceFeePct,
      row?.minServiceFee,
      row?.min_service_fee,
      raw?.min_service_fee
    ) ?? 0;

  const netAdvance =
    readNumber(row?.netAdvance, row?.net_advance, raw?.net_advance) ??
    Math.max(0, Math.round(facility * (ipPercent / 100)));

  const arrangementFee =
    readNumber(
      row?.arrangementFeeAmount,
      row?.arrangement_fee,
      raw?.arrangement_fee
    ) ?? 0;

  const apr =
    readNumber(row?.ratePct, row?.apr, row?.aprc, raw?.apr) ??
    discountRatePercent;

  const ledger = String(
    filters.fundingLedgerType ||
      row?.part_full_fund_ledger ||
      raw?.part_full_fund_ledger ||
      ""
  )
    .trim()
    .toLowerCase();

  const confidentialFacility = toFlag01(
    filters.confidentialFacility ??
      row?.confidential_facility ??
      raw?.confidential_facility ??
      raw?.confidential
  );
  const confidential = toFlag01(
    row?.confidential ?? raw?.confidential ?? confidentialFacility
  );
  const disclosure = toFlag01(
    row?.disclosure ??
      raw?.disclosure ??
      (confidentialFacility ? 0 : 1)
  );

  // facility_type = Confidential / Disclosure (NOT part/full ledger)
  const facilityType =
    confidentialFacility || confidential ? "Confidential" : "Disclosure";
  const fundingLedgerType =
    ledger === "part" ? "Part" : ledger === "full" ? "Full" : null;

  const sectorValue =
    String(sector || row?.sector || raw?.sector || "").trim() || null;

  let termMonths =
    readNumber(row?.termMonths, row?.contract_term_months, raw?.contract_term_months) ?? 12;

  const abCalc = row?.abCalc || null;
  let additionalBorrowingAmount = 0;
  if (abCalc?.abPrincipal != null && Number(abCalc.abPrincipal) > 0) {
    additionalBorrowingAmount = Math.round(Number(abCalc.abPrincipal));
  } else {
    const userAb = Number(additionalBorrowingNum) || 0;
    if (userAb > 0) {
      additionalBorrowingAmount =
        additionalBorrowingType === "percent"
          ? Math.round((facility * userAb) / 100)
          : Math.round(userAb);
    }
  }

  // Facility contract term stays on contract_term_months — do NOT replace with AB term
  const displayTitle = formatInvoiceFinanceProductTitle({
    facility_type: facilityType,
    confidential_facility: confidentialFacility,
    confidential,
    disclosure,
    contract_term_months: termMonths,
    termMonths,
  });

  const productData = {
    id,
    name: displayTitle,
    product_id: id,
    mortgage_product: id,
    product_name: displayTitle,
    lender_id: raw?.lender_id || raw?.lender || id,
    lender_name: row?.lenderName || raw?.lender_name || "Unknown Lender",
    lenderId: raw?.lender_id || raw?.lender || id,
    lenderName: row?.lenderName || raw?.lender_name || "Unknown Lender",
    category: "IF",
    product_category: "IF",
    // Frontend routing helper only — never send as Shortlisted Product.product_type
    // (that field is Fixed/Tracker/Variable/Discounted).
    productType: "IF",
    product_type: "",
    loan_purpose: "Invoice Finance",
    loanPurpose: "Invoice Finance",

    // IF Application / shortlist fields
    sector: sectorValue,
    projected_turnover: turnover,
    facility_required: facility,
    facility_type: facilityType,
    part_full_fund_ledger: fundingLedgerType,
    net_advance: netAdvance,
    additional_borrowing_amount: additionalBorrowingAmount,
    additional_borrowing: additionalBorrowingAmount > 0 ? 1 : 0,
    confidential_facility: confidentialFacility,
    confidential,
    disclosure,
    ip_percent: ipPercent,
    discount_rate_percent: discountRatePercent,
    simple_service_fee: simpleServiceFee,
    min_service_fee: minServiceFee,
    apr,
    arrangement_fee: arrangementFee,

    // Display / cost helpers used by shortlist UI
    invoicePurchasePct: ipPercent,
    ratePct: apr,
    rate: apr,
    interest_rate:
      additionalBorrowingAmount > 0 && abCalc?.interestRatePct != null
        ? abCalc.interestRatePct
        : discountRatePercent,
    netAdvance,
    minServiceFee,
    costOverProductTerm:
      readNumber(row?.costOverProductTerm, row?.cost_over_product_term) ?? 0,
    cost_over_product_term:
      readNumber(row?.costOverProductTerm, row?.cost_over_product_term) ?? 0,
    total_cost:
      readNumber(row?.costOverProductTerm, row?.cost_over_product_term) ?? 0,
    totalCost:
      readNumber(row?.costOverProductTerm, row?.cost_over_product_term) ?? 0,
    termMonths,
    term_months: termMonths,
    contract_term_months: termMonths,
    headlineAmount: readNumber(row?.headlineAmount, facility) ?? facility,
    headline_amount: readNumber(row?.headlineAmount, facility) ?? facility,
    loan_amount: facility,
    // Snapshot of sourcing full-details economics (so cart matches IF sourcing page)
    annualCostPct:
      readNumber(row?.annualCostPct, row?.serviceFeePct, simpleServiceFee) ?? simpleServiceFee,
    utilisationPct: readNumber(row?.utilisationPct, row?.utilisation_pct) ?? null,
    utilisedFacility: readNumber(row?.utilisedFacility, row?.utilised_facility) ?? null,
    discountFeeAmount: readNumber(row?.discountFeeAmount, row?.discount_fee_amount) ?? null,
    serviceFeeAmount: readNumber(row?.serviceFeeAmount, row?.service_fee_amount) ?? null,
    arrangementFeeAmount:
      readNumber(row?.arrangementFeeAmount, row?.arrangement_fee_amount, arrangementFee) ??
      arrangementFee,
    renewalFeeAmount: readNumber(row?.renewalFeeAmount, row?.renewal_fee_amount) ?? null,
    fixedFeeAmount: readNumber(row?.fixedFeeAmount, row?.fixed_fee_amount) ?? null,
    reviewLimit: (() => {
      if (!(facility > 0)) return null;
      return Math.ceil((facility * 1.1) / 10000) * 10000;
    })(),
    review_limit: (() => {
      if (!(facility > 0)) return null;
      return Math.ceil((facility * 1.1) / 10000) * 10000;
    })(),
    dpAllowed: row?.dpAllowed || raw?.dp_allowed || "Yes",
    addedAt: Date.now(),
    _raw: raw,
    _sourceRow: row,
  };

  if (additionalBorrowingAmount > 0 && abCalc) {
    productData.abCalc = abCalc;
    productData.additional_borrowing_interest_rate = abCalc.interestRatePct ?? null;
    productData.interest_rate = abCalc.interestRatePct ?? productData.interest_rate;
    productData.io_or_capital_repayment = abCalc.repaymentType || null;
    productData.monthly_repayments = abCalc.monthlyRepayment ?? 0;
    productData.monthly_payment = abCalc.monthlyRepayment ?? 0;
    productData.monthlyCost = abCalc.monthlyRepayment ?? 0;
    productData.total_repayable = abCalc.totalRepayable ?? 0;
    productData.first_12_month_interest = abCalc.first12MonthsInterest ?? abCalc.annualInterest ?? 0;
    productData.ab_arrangement_fee = abCalc.arrangementFee ?? 0;
    productData.arrangement_fee_term =
      abCalc.arrangementFeePct ??
      readNumber(raw?.arrangement_fee_term, row?.arrangement_fee_term, productData.arrangement_fee_term) ??
      null;
    productData.arrangementFeeTerm = productData.arrangement_fee_term;
    productData.ab_term_months = abCalc.termMonths ?? null;
    productData.ab_principal = abCalc.abPrincipal ?? null;
    // Backend Applications field name for AB cost over product term
    productData.ab_cost_over_product_term = abCalc.costOverTerm ?? 0;
    // Keep legacy alias for older shortlist/localStorage rows
    productData.ab_cost_over_term = productData.ab_cost_over_product_term;
  }

  return productData;
}

export function mapSourcingApiProducts(products, context) {
  return (products || []).map((product) => {
    const row = mapInvoiceFinanceToSourcingRow(product, context);
    const isScheduled = isInvoiceFinanceProductScheduled(product);
    return {
      ...row,
      status: isScheduled
        ? "Scheduled"
        : product?.product_status || product?.status || row._raw?.status || "Active",
      _isScheduled: isScheduled,
      product_schedule: product?.product_schedule ?? product?._raw?.product_schedule,
      date_time: product?.date_time ?? product?._raw?.date_time,
    };
  });
}

/** Keep products whose min/max facility band includes the required facility. */
export function filterProductsByFacilityBand(rows, facilityNum) {
  const facility = Number(facilityNum) || 0;
  if (facility <= 0) return rows || [];
  return (rows || []).filter((row) => productMatchesFacilityBand(row, facility));
}

/** @deprecated Use filterProductsByFacilityBand */
export function filterProductsByTurnoverBand(rows, turnoverNum) {
  return filterProductsByFacilityBand(rows, turnoverNum);
}

/** Filter raw API product rows by facility band before mapping. */
export function filterApiProductsByFacilityBand(products, facilityNum) {
  return filterProductsByFacilityBand(products, facilityNum);
}

/** @deprecated Use filterApiProductsByFacilityBand */
export function filterApiProductsByTurnoverBand(products, turnoverNum) {
  return filterApiProductsByFacilityBand(products, turnoverNum);
}